import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IInvoiceSettingsRes, useInvoiceSettingsQuery } from '../../../../hooks/query/useInvoiceSettingsQuery';
import { PlanUpgradeFlow } from '../../../../flows/planUpgrade';
import { useStore } from '../../../../store';
import { OnboardingContext } from '../InvoiceOnboarding';
import { FirstImpressionPage } from '../../../../components/FirstImpressionPage/FirstImpressionPage';
import {
  FirstImpressionReportClickCtaAfterAnimation,
  FirstImpressionReportStartAnimation,
} from '../../../../services/firstImpressionAnalytics.service';
import { ISummaryData } from '../../../../types';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../../constants/general';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

export const StepIntro: React.FC = () => {
  const flowContext = useContext(OnboardingContext);
  const { onStepSubmit, isLoadingState } = flowContext;
  const navigate = useNavigate();
  const { customerId } = useStore((state) => state.user);

  const {
    actionFlags: { createInvoice },
  } = useStore((state) => state.getSummaryData());
  const [key, setKey] = useState(0); // State to force re-render
  const summaryData: ISummaryData = useStore((state) => state.summary);
  const planType = summaryData.customer.customerProfile;
  const [finishedCustomization, setFinishedCustomization] = useState(false);
  const [shouldShowUpgrade] = useState<boolean>(
    canUpgradeToUseFeature(FEATURE_FLAG.INVOICE) && !validateFeatureFlag(FEATURE_FLAG.INVOICE),
  );
  const [isPlanUpgradeFlowOpen, setIsPlanUpgradeFlowOpen] = useState(false);
  const invoicesPermission = featureAccess(FEATURE_FLAG.INVOICE, LimitedFeatureType.Invoices);
  const planUpgradePermission = featureAccess(FEATURE_FLAG.PLAN_UPGRADE, LimitedFeatureType.UpgradePlan);

  useEffect(() => {
    FirstImpressionReportStartAnimation({ customerProfilePLan: planType, value: 'invoice' });
  }, []);
  const { t } = useTranslation('firstImperssionEN');
  const [finishedState, setFinishedState] = useState(false);
  const handleLottieClick = () => {
    if (!finishedState) return;
    setKey((prevKey) => prevKey + 1);
    setFinishedState(false); // Update the key to force re-render
  };
  const { refetch: fetchInvoiceSettingsData } = useInvoiceSettingsQuery({
    customerId,
    onSuccess: (invoiceSettingsDataResponse: IInvoiceSettingsRes) => {
      setFinishedCustomization(invoiceSettingsDataResponse?.finishCustomization);
    },
  });
  useEffect(() => {
    fetchInvoiceSettingsData();
  }, []);
  const eventListeners = [
    {
      event: 'complete',
      callback: () => {
        setFinishedState(true);
      },
    },
  ];
  const handleButtonClick = () => {
    FirstImpressionReportClickCtaAfterAnimation({ customerProfilePLan: planType, value: 'invoice' });
    if (shouldShowUpgrade) {
      setIsPlanUpgradeFlowOpen(true);
    } else if (!finishedCustomization) {
      onStepSubmit();
    } else if (!createInvoice) {
      navigate('/invoice/create');
    }
  };
  const title1 = t('invoice.title1');
  const title2 = t('invoice.title2');
  const buttonText = !shouldShowUpgrade ? t('invoice.button') : t('invoice.upgradeButton');
  const isButtonDisabled = shouldShowUpgrade
    ? planUpgradePermission !== FeatureAccess.READ_WRITE
    : invoicesPermission !== FeatureAccess.READ_WRITE;
  return (
    <>
      <FirstImpressionPage>
        <FirstImpressionPage.Header
          texts={[title1, title2]}
          isLoadingState={isLoadingState}
          onSubmit={handleButtonClick}
          buttonText={buttonText}
          buttonId='elCtaGetStarted'
          buttonDisabled={isButtonDisabled}
        />
        <FirstImpressionPage.LottieSection
          key={key} // Use the key to force re-render
          eventListeners={eventListeners}
          lottie='https://lili-mobile-images.s3.us-east-1.amazonaws.com/webapp/lottie/first_impression_invoice.json'
          isLoop={false}
          onLottieClick={handleLottieClick}
          finishedState={finishedState}
        />
      </FirstImpressionPage>
      {isPlanUpgradeFlowOpen && (
        <PlanUpgradeFlow
          onCloseClick={() => {
            setIsPlanUpgradeFlowOpen(false);
          }}
          origin='categorization'
        />
      )}
    </>
  );
};
